// encounter-map/ft3d/light-presence.ts — encounter_light_types_present (ability_environment_condition_helpers.php) twin (2026-09-25,
// Janus C1 / R-042): the SET of light-type UUIDs present at ANY point of an encounter — the backing for the if_light_type_active /
// if_light_type_not_active ability gates. Candidate points (each source's centre, 8-direction rings straddling every state boundary,
// pairwise centre-line midpoints between every two sources' boundary crossings, and one point beyond every source's reach) are each
// resolved through calculateLightAt, exactly as PHP samples them.
// ⚠ Geometry follows the MAP (x_pos / y_pos ground plane — the perception collector, PHP's `'y_pos'` callers). PHP's gate still calls
// collect_light_and_dark_sources() with its legacy default `'z_pos'` (elevation) axis, which the 3D map never uses; the TS truth is the
// map's geometry (docs/JANUS_CLIENT_TRUTH_STATUS.md bug ledger 2026-09-25).
import { calculateLightAt, collectLightAndDarkSources, PERCEPTION_LIGHT } from './perception';
import { effectiveEncounterLightId } from '../../shared/env-types';

type Row = Record<string, unknown>;
const KNOWN = new Set<string>(Object.values(PERCEPTION_LIGHT));

/** null = the base light is unknown (the gate is skipped, as PHP's caller fails open). */
export function encounterLightTypesPresent(encounterArr: Row | null, campaignArr: unknown, memberFallbackLight?: unknown,
    heldSources?: ReturnType<typeof collectLightAndDarkSources>): string[] | null {
    let base: unknown = encounterArr ? effectiveEncounterLightId(encounterArr, campaignArr) : null;
    if (typeof base !== 'string' || base === '') base = memberFallbackLight; // fetch_encounter_creatures' stamped encounter_light
    if (typeof base !== 'string' || base === '') return null;
    base = base.toUpperCase();
    if (!KNOWN.has(base as string)) return null;
    const baseLight = base as string;
    if (!heldSources && (!encounterArr || encounterArr.encounter_creatures_arr === undefined || encounterArr.encounter_inventories_arr === undefined)) return [baseLight];
    const { lightSources, darkSources } = heldSources ?? collectLightAndDarkSources(encounterArr!);
    if (!lightSources.length && !darkSources.length) return [baseLight];
    const angles: [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1], [0.7071, 0.7071], [-0.7071, 0.7071], [0.7071, -0.7071], [-0.7071, -0.7071]];
    const samples: [number, number][] = [];
    const addRings = (cx: number, cz: number, r: number, factors: number[]): void => {
        samples.push([cx, cz]);
        for (const f of factors) for (const [ux, uz] of angles) samples.push([cx + ux * r * f, cz + uz * r * f]);
    };
    let far = 0;
    for (const s of lightSources) {
        const r = Math.abs(s.radius);
        addRings(s.x, s.z, r, [0.5, 0.99, 1.01, 1.5, 1.99, 2.01]);
        far = Math.max(far, Math.abs(s.x) + 2 * r, Math.abs(s.z) + 2 * r);
    }
    for (const s of darkSources) {
        const r = Math.abs(s.radius);
        addRings(s.x, s.z, r, [0.5, 0.99, 1.01]);
        far = Math.max(far, Math.abs(s.x) + r, Math.abs(s.z) + r);
    }
    const bounded = [...lightSources.map(s => ({ x: s.x, z: s.z, radii: [Math.abs(s.radius), 2 * Math.abs(s.radius)] })),
        ...darkSources.map(s => ({ x: s.x, z: s.z, radii: [Math.abs(s.radius)] }))];
    for (let i = 0; i < bounded.length; i++) for (let j = i + 1; j < bounded.length; j++) {
        const a = bounded[i]!, b = bounded[j]!;
        const dx = b.x - a.x, dz = b.z - a.z, d = Math.sqrt(dx * dx + dz * dz);
        const [ux, uz] = d > 0.0001 ? [dx / d, dz / d] : [1, 0];
        const crossings: number[] = [];
        for (const r of a.radii) crossings.push(r, -r);
        for (const r of b.radii) crossings.push(d + r, d - r);
        crossings.sort((p, q) => p - q);
        for (let k = 1; k < crossings.length; k++) { const t = (crossings[k - 1]! + crossings[k]!) / 2; samples.push([a.x + ux * t, a.z + uz * t]); }
    }
    samples.push([far + 1000, 0]);
    const present: string[] = [];
    for (const [x, z] of samples) {
        const light = calculateLightAt(x, z, baseLight, lightSources, darkSources);
        if (!present.includes(light)) present.push(light);
        if (present.length === 4) break;
    }
    return present;
}
